import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useCommandActions } from '../../features/command/registry.js';
import { useShortcuts } from '../../lib/keyboard/use-shortcuts.js';
import { holdsPermission } from '../../session/permissions.js';
import { meQueryOptions } from '../../session/use-current-user.js';

/**
 * The supervision area.
 *
 * The permission is checked without a queue or a team, because a persona link names neither: a
 * supervisor of one team may come in, and the boards inside then ask which queues and teams are in
 * their scope. The API refuses anything either of those got wrong.
 */
export const Route = createFileRoute('/supervise')({
  async beforeLoad({ context }) {
    const me = await context.queryClient.ensureQueryData(meQueryOptions());
    if (!holdsPermission(me, 'agents.supervise')) {
      throw redirect({ to: '/', search: { denied: 'agents.supervise' } });
    }
  },
  component: SuperviseLayout,
});

/**
 * The area's layout, and the keyboard that belongs to it.
 *
 * The two boards are bound here rather than on each board, because the point of the keys is to get
 * from one to the other: a supervisor watching a queue fill up presses `Alt+G` to see who is free
 * and `Alt+Q` to come back. Binding them on the boards themselves would mean each only worked from
 * the other one.
 */
function SuperviseLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const toQueues = () => void navigate({ to: '/supervise/queues' });
  const toAgents = () => void navigate({ to: '/supervise/agents' });

  useShortcuts('board', { 'board.queues': toQueues, 'board.agents': toAgents });
  useCommandActions([
    {
      id: 'board.queues',
      group: 'goTo',
      label: t('shortcut.queues'),
      shortcutId: 'board.queues',
      run: toQueues,
    },
    {
      id: 'board.agents',
      group: 'goTo',
      label: t('shortcut.agents'),
      shortcutId: 'board.agents',
      run: toAgents,
    },
  ]);

  return (
    <div className="flex h-full flex-col">
      <Outlet />
    </div>
  );
}
